import { useState, useEffect } from "react"
import { Menu, X, Globe, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/je-button"
import { useLanguage } from "@/contexts/LanguageContext"

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const { language, setLanguage, t } = useLanguage()

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 16)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const navLinks = [
    { href: "/#services", label: t('nav.services') },
    { href: "/#process", label: t('nav.process') },
    { href: "/#projects", label: t('nav.projects') },
    { href: "/#faq", label: t('nav.faq') },
  ]

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith('/#')) return
    e.preventDefault()
    if (window.location.pathname !== '/') { window.location.href = href; return }
    const id = href.split('#')[1]
    const el = id ? document.getElementById(id) : null
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 80
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
    setIsOpen(false)
  }

  const cycleLang = () => setLanguage(language === 'fr' ? 'en' : language === 'en' ? 'de' : 'fr')

  return (
    <>
      <nav className={`fixed top-0 inset-x-0 z-50 nav-modern ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container-editorial">
          <div className="flex items-center justify-between h-20">
            <a href="/" className="flex items-center gap-2 group">
              <span className="w-8 h-8 rounded-sm bg-je-red flex items-center justify-center text-je-white font-serif font-bold text-lg leading-none">JE</span>
              <span className="font-serif text-lg font-medium text-je-black tracking-tight">
                HEPIA<span className="text-je-red">.</span>
              </span>
            </a>

            <div className="hidden lg:flex items-center gap-10">
              {navLinks.map(l => (
                <a key={l.href} href={l.href} onClick={(e) => handleNavClick(e, l.href)}
                  className="text-sm font-medium text-je-navy/80 hover:text-je-red transition-colors relative">
                  {l.label}
                </a>
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              <button onClick={cycleLang} aria-label="Switch language"
                className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-je-navy/70 hover:text-je-red transition-colors">
                <Globe size={14} /> {language}
              </button>
              <Button size="sm" asChild>
                <a href="/#contact" className="gap-1.5">
                  {t('nav.launchProject')}
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </Button>
            </div>

            <button onClick={() => setIsOpen(!isOpen)} className="lg:hidden p-2 text-je-navy" aria-label="Menu">
              {isOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </nav>

      <div className={`mobile-menu ${isOpen ? 'open' : ''} lg:hidden`}>
        <div className="container-editorial pt-24 pb-12 h-full flex flex-col">
          <div className="flex flex-col gap-1 border-t border-je-navy/10">
            {navLinks.map(l => (
              <a key={l.href} href={l.href} onClick={(e) => handleNavClick(e, l.href)}
                className="font-serif text-3xl py-5 border-b border-je-navy/10 text-je-black flex items-center justify-between group">
                {l.label}
                <ArrowRight className="w-5 h-5 text-je-red opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            ))}
          </div>
          <div className="mt-auto space-y-4">
            <Button size="lg" className="w-full" asChild>
              <a href="/#contact" onClick={() => setIsOpen(false)}>{t('nav.launchProject')}</a>
            </Button>
            <div className="flex items-center justify-between text-sm font-mono uppercase tracking-wider text-je-gray-dark">
              <button onClick={cycleLang} className="flex items-center gap-1.5"><Globe size={14} /> {language}</button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
export default Navigation
